import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls"
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import {
  createMachine,
  State,
  actions,
  assign,
  send,
  sendParent,
  interpret,
  spawn
} from 'xstate';
import { fromEvent } from 'rxjs';

import _chunk from 'lodash/chunk'

const TWEEN = require('@tweenjs/tween.js')

const WIDTH = 1902
const HEIGHT = 2458

const windowResize$ = fromEvent(window, 'resize');
const mouseMove$ = fromEvent(document.body, 'mousemove');

const createWindowMachine = () => createMachine({
  id: 'window',
  invoke: {
    src: (context, event) => windowResize$
  },
  on: {
    resize: {
      actions: ['sendResize']
    }
  }
}, {
  actions: {
    sendResize: sendParent('WINDOW_RESIZE')
  }
});

const createMouseMachine = () => createMachine({
  id: 'mouse',
  invoke: {
    src: (context, event) => mouseMove$
  },
  on: {
    mousemove: {
      actions: ['sendMouseMove']
    }
  }
}, {
  actions: {
    sendMouseMove: sendParent((context, event) => ({ x: event.x, y: event.y, type: 'MOUSE_MOVE' }))
  }
});

const createLightMachine = (context) => createMachine({
  id: 'lights',
  initial: 'create',
  context,
  states: {
    create: {
      always: [ { target: 'animate', actions: [ 'createLights' ] } ]
    },
    animate: {
      entry: ['startAnimation'],
      on: {
        MOUSE_MOVE: {
          // target: 'followMouse',
          actions: ['pauseAnimation', 'cancelReAnimate', 'sendReAnimate', 'moveLight']
        },
        RE_ANIMATE: {
          actions: ['startAnimation']
        }
      }
    },
  },
}, {
  actions: {
    test: (context, event) => {
      console.log(event)
    },
    sendReAnimate: actions.send('RE_ANIMATE', { delay: 1000, id: 'start-animation' }),
    cancelReAnimate: actions.cancel('start-animation'),
    moveLight: assign({
      spotLight: ({ spotLight, tween }, event) => {
        const rotY = clamp(event.x, 0, window.innerWidth, -.4, .4);
        const rotX = clamp(event.y, 0, window.innerHeight, -1, 0);

        spotLight.rotation.y = rotY
        spotLight.rotation.x = rotX

        return spotLight;
      }
    }),
    pauseAnimation: assign({
      tween: ({ tween }) => {
        tween.stop();
        return tween;
      }
    }),
    startAnimation: assign({
      tween: ({ spotLight, tween }, event) => {
        const { z } = spotLight.rotation

        const roty = .4
        const a = new TWEEN.Tween(spotLight.rotation)
          .to({ x: degToRads(-30), y: -roty / 2, z, }, 2500)
          .easing(TWEEN.Easing.Elastic.InOut);
        
        const b = new TWEEN.Tween(spotLight.rotation)
          .to({ x: degToRads(-30), y: roty / 2, z, }, 2500)
          .delay(500)
          .easing(TWEEN.Easing.Elastic.InOut);

        a.chain(b)
        b.chain(a)

        if (tween) {
          const res = new TWEEN.Tween(spotLight.rotation)
            .to({ x: degToRads(-30), y: roty / 2, z }, 2500)
            .easing(TWEEN.Easing.Elastic.InOut);

          res.chain(a)
          a.chain(b)
          b.chain(a)
          res.start();
          return res;
        }

        a.start();

        return a;
      }
    }),
    createLights: assign({
      spotLight: ({ scene }, event) => {
        const group = new THREE.Group();

        const light = new THREE.PointLight( 0xffffff, 0.75 );
        light.castShadow = true;
        group.add(light)
        light.position.set(0,0, 2200);

        // const geometry = new THREE.BoxGeometry(100, 100);
        // const material = new THREE.MeshBasicMaterial();
        // const art = new THREE.Mesh( geometry, material );
        // art.position.set(0,0, 100);
        // group.add(art)
        scene.add( group );
        group.rotateX(degToRads(-30))
        const roty = .4
        group.rotateY(group.rotation.y + roty / 2)

        return group;
      },
      hemiLight: ({ scene }, event) => {
        const hemilight = new THREE.HemisphereLight( 0xffffbb, 0x080820, 0.1 );
        scene.add( hemilight );

        return hemilight;
      }
    }),
  }
});

const loadImage = (img) => new Promise((resolve, reject) => {
  const loader = new THREE.TextureLoader();
  loader.load(
    img,
    function ( texture ) {
       resolve(texture);
    },
    undefined,
    function ( err ) {
      console.log(err)
      reject( 'An error happened.' );
    }
  );
})

const loadImages = async (images) => {
  // console.log(images)
  const proms = images.map(loadImage);
  // console.log(proms)
  const imgs = await Promise.all(proms)
  // console.log(imgs)
  return imgs
}

const degToRads = (deg) => deg * (Math.PI / 180)

const clamp = (value, x1, y1, x2, y2) => (value - x1) * (y2 - x2) / (y1 - x1) + x2;

let gameService = null;

const setupMachine = ()  => createMachine({
  id: 'AssetsSetup',
  initial: 'scene',
  context: {
    images: [],
    view: {
      width: null,
      height: null,
    },
    scene: null,
    camera: null,
    renderer: null,
    gameBoard: null,
  },
  states: {
    scene: {
      always: [ { target: 'camera', actions: [ 'createScene' ] } ]
    },
    camera: {
      always: [ { target: 'renderer', actions: [ 'createCamera' ] } ]
    },
    renderer: {
      always: [ { target: 'controls', actions: [ 'createRenderer' ] } ]
    },
    controls: {
      always: [ { target: 'lights', actions: [ 'createOrbitControls' ] } ]
    },
    lights: {
      always: [ { target: 'loadTetxures', actions: [ 'createLights' ] } ]
    },
    loadTetxures: {
      invoke: {
        src: ({ images }, event) => loadImages(images),
        onDone: {
          target: 'done',
          actions: ['setTextures']
        },
        onError: {}
      },
    },
    done: {
      type: 'final',
      data: {
        scene: (context, event) => context.scene,
        camera: (context, event) => context.camera,
        renderer: (context, event) => context.renderer,
        art: (context, event) => context.art,
        lights: (context, event) => context.lights,
        orbitControls: (context, event) => context.orbitControls,
      },
    }
  }
},
{
  actions: {
    createScene: assign({
      scene: (context, event) => {
        const scene = new THREE.Scene()
        return scene
      }
    }),
    createCamera: assign({
      camera: (context, event) => {
        const aspectRatio = context.view.width / context.view.height;
        const camera = new THREE.PerspectiveCamera( 75, aspectRatio, 0.1, 4500 );
        // console.log(context.view.width)
        // console.log(context.view.height)
        // console.log(context.view.width / context.view.height)
        if (aspectRatio < .6) {
          camera.position.z = 1500;
        }else {
          camera.position.z = 1700;
        }
        return camera;
      }
    }),
    createRenderer: assign({
      renderer: (context, event) => {
        const renderer = new THREE.WebGLRenderer({ alpha: true });
        renderer.setClearColor(0xffffff, 0);
        renderer.setPixelRatio(window.devicePixelRatio ? window.devicePixelRatio : 1);
        renderer.shadowMap.enabled = true;
        renderer.setSize( context.view.width, context.view.height );
        document.body.appendChild( renderer.domElement );
        return renderer;
      }
    }),
    createOrbitControls: assign({
      orbitControls: (context, event) => {
        const { camera, renderer } = context;
        const orbitControls = new OrbitControls(camera, renderer.domElement);
        orbitControls.maxDistance = 3000; // How far you can dolly out
        orbitControls.maxPolarAngle = degToRads(100); // How far you can orbit vertically, upper limit. Range is 0 to Math.PI radians, and default is Math.PI.
        orbitControls.maxAzimuthAngle = Math.PI * .15 // How far you can orbit horizontally, lower limit. If set, the interval [ min, max ] must be a sub-interval of [ - 2 PI, 2 PI ], with ( max - min < 2 PI ). Default is Infinity.
        
        orbitControls.minDistance = 100;
        orbitControls.minPolarAngle = degToRads(80);
        orbitControls.minAzimuthAngle = -Math.PI * .15;
        
        orbitControls.rotateSpeed = 0.25;
        orbitControls.zoomSpeed = 0.25;

        orbitControls.enablePan = false;
        
        // let camTween, contTween

        // const default_camera_position = { posX: camera.position.x, posY: camera.position.y, posZ: camera.position.z };
        // const default_camera_rotation = { rotX: camera.rotation.x, rotY: camera.rotation.y, rotZ: camera.rotation.z }
        // const default_controls_target = { ...orbitControls.target };
        // debugger
        let timeout

        orbitControls.addEventListener('end', () => {
          timeout = setTimeout(() => {
            // console.log(camera)
            // debugger
            orbitControls.reset();
          }, 1000)
          // orbitControls.reset();
          // debugger
          // const rot = new TWEEN.Tween({ ...camera.postion, ...camera.rotation })
          //   .onStart(() => {
          //     orbitControls.enabled = false

          //     // TWEEN.Tween(camera.position)
          //     //   .to(default_camera_position, 500)
          //     //   .easing(TWEEN.Easing.Elastic.InOut)
          //     //   .start()
          //   })
          //   .to({ ...default_camera_position, ...default_camera_rotation }, 500)
          //   .easing(TWEEN.Easing.Elastic.InOut)
          //   .onComplete(() => {
          //     orbitControls.reset();
          //     orbitControls.enabled = true
          //   })
          //   .start()

          // const pos = new TWEEN.Tween(camera.position)
          //   .to(default_camera_position, 500)
          //   .easing(TWEEN.Easing.Elastic.InOut)
          //   .onComplete(() => {
          //     orbitControls.reset();
          //     orbitControls.enabled = true
          //   })

            // camTween = rot.chain(pos).start()

          // camTween = rot.chain(pos).start();
            // .start();

          // contTween = new TWEEN.Tween(orbitControls.target)
          //   .to(orbitControls.position0, 2500)
          //   .easing(TWEEN.Easing.Elastic.InOut)
          //   .start();
        })

        orbitControls.addEventListener('start', () => {
          // camTween && camTween.stop();
          clearTimeout(timeout)
        })
        
        return orbitControls;
      }
    }),
    createLights: assign({
      lights: (context, event) => createLightMachine(context)
    }),
    setTextures: assign({
      art: (context, event) => {
        const [ map, normalMap, alphaMap ] = event.data;
        const geometry = new THREE.PlaneGeometry(WIDTH, HEIGHT);
        const material = new THREE.MeshPhongMaterial( {
          color: 0xFFFFFF,
          map,
          // displacementMap,
          // displacementBias: 10,
          // displacementScale: -100,
          normalMap,
          alphaMap,
          shininess: 50
         } );
        const art = new THREE.Mesh( geometry, material );
        context.scene.add( art );
        return art;
      }
    }),
  }
});

const slaterMachine = createMachine({
  id: 'Slater',
  initial: 'setup',
  context: {
    view: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    images: [
      'slater_line_work.jpg',
      'slater_normal_map.jpg',
      'slater_alpha_map.jpg',
      // 'slater_displacement_map.jpg'
      // 'test_displacement.jpeg',
    ],
  },
  states: {
    setup: {
      invoke: {
        src: (context, event) => setupMachine(),
        data: {
          view: (context, event) => context.view,
          images: (context, event) => context.images,
        },
        onDone: {
          target: 'listeners',
          actions: ['setAssets']
        },
      }
    },
    listeners: {
      always: [ { target: 'view', actions: [ 'setupListeners' ] } ]
    },
    view: {
      on: {
        TICK: { actions: ['render'] },
        WINDOW_RESIZE: { actions: ['resizeWindow'] },
        MOUSE_MOVE: { actions: ['moveSpotLight'] },
      }
    },
  },
},
{
  actions: {
    setAssets: assign({
      scene: (context, event) => event.data.scene,
      camera: (context, event) => event.data.camera,
      renderer: (context, event) => event.data.renderer,
      art: (context, event) => event.data.art,
      lights: (context, event) => spawn(event.data.lights),
      orbitControls: (context, event) => event.data.orbitControls,
    }),
    render: (context, event) => {
      const { renderer, scene, camera } = context;
      TWEEN.update();
      renderer.render( scene, camera );
    },
    setupListeners: assign({
      windowMachine: (context, event) => spawn(createWindowMachine()),
      mouseMachine: (context, event) => spawn(createMouseMachine()),
    }),
    resizeWindow: (context, event) => {
      const { renderer, camera } = context;
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();

      renderer.setSize( window.innerWidth, window.innerHeight );
    },
    moveSpotLight: send((context, event) => event, { to: (context) => context.lights })
  }
});

export const service = interpret(slaterMachine)

// gameService = interpret(slaterMachine);
// gameService.start();

// const animate = function () {
//   requestAnimationFrame( animate );
//   TWEEN.update()
//   gameService.send('TICK');
// };

// animate();